import React from 'react';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const NewsCard = ({ article, featured = false }) => {
  const isExternal = article.url?.startsWith('http');

  const CardWrapper = ({ children }) => {
    const commonClasses = `group flex flex-col transition-all duration-300 overflow-hidden`;
    const specificClasses = featured 
      ? `md:flex-row md:items-stretch bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md` 
      : `h-full bg-white border border-gray-200 rounded-lg hover:shadow-md`;

    if (isExternal) {
      return (
        <a 
          href={article.url} 
          target="_blank" 
          rel="noopener noreferrer"
          className={`${commonClasses} ${specificClasses}`}
        >
          {children}
        </a>
      );
    }
    return (
      <div className={`${commonClasses} ${specificClasses}`}>
        {children}
      </div>
    );
  };

  return (
    <CardWrapper>
      {/* Image Container */}
      <div className={`relative overflow-hidden shrink-0 ${
        featured 
          ? 'w-full md:w-1/2 lg:w-[55%] h-64 md:h-auto' 
          : 'h-48 w-full'
      }`}>
        <img 
          src={article.image} 
          alt={article.title}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {featured && (
          <div className="absolute top-4 left-4 md:hidden">
            <span className="bg-primary text-white text-[10px] font-bold px-2 py-1 rounded uppercase tracking-wider">
              Featured
            </span>
          </div>
        )}
      </div>

      {/* Content Container */}
      <div className={`flex flex-col justify-center ${
        featured 
          ? 'w-full md:w-1/2 lg:w-[45%] p-6 md:p-8 lg:p-10' 
          : 'p-5'
      }`}>
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[10px] md:text-xs font-bold uppercase tracking-widest text-accent">
            {article.category}
          </span>
          <span className="text-gray-300 text-xs">•</span>
          <span className="text-gray-500 text-[10px] md:text-xs font-medium">{article.date}</span>
        </div>

        <h3 className={`font-bold text-gray-900 mb-3 group-hover:text-primary transition-colors leading-tight ${
          featured ? 'text-xl md:text-2xl lg:text-3xl' : 'text-lg'
        }`}>
          {article.title}
        </h3>

        <p className={`text-gray-600 mb-6 line-clamp-3 leading-relaxed ${
          featured ? 'text-sm md:text-base' : 'text-sm'
        }`}>
          {article.excerpt}
        </p>

        <div className="mt-auto pt-4 border-t border-gray-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-xs text-gray-500 font-medium">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiUser} className="text-gray-400 text-[10px]" />
            </div>
            <span>{article.author}</span>
          </div>
          {isExternal && (
            <div className="flex items-center gap-1 text-primary text-xs font-bold uppercase tracking-wider">
              <span className="hidden sm:inline">Read Source</span>
              <SafeIcon icon={FiIcons.FiArrowRight} className="text-sm transform group-hover:translate-x-1 transition-transform" />
            </div>
          )}
        </div>
      </div>
    </CardWrapper>
  );
};

export default NewsCard;